import classNames from "classnames";
import { createContext, useContext, useState } from "react";
import UserContext from "../../context/user";
import { Link } from "react-router-dom";
import LanguageContext from "../../context/language";
import Icon from "../icon";
import ShareWidget from "../share-widget";

function getTabDescription(tabUrl, labels) {
  if (!tabUrl.startsWith("/")) {
    return "";
  }

  const item = tabUrl.split("/")[1];

  return labels?.[`MENU_DESCRIPTION_${item.toUpperCase()}`] ?? "";
}

export const myHubsPageUrl = "/my-hubs";
export const HeaderContext = createContext({
  openTab: "",
  menuOpen: false,
  menu: {},
  setOpenTab: () => null,
  setMenuOpen: () => null,
  selectedLanguage: {},
  cmsLanguages: [],
  updateLanguage: () => null,
  openUserSubmenu: false,
  setOpenUserSubmenu: () => null,
});

export function UserImage(props) {
  const { userProfile } = useContext(UserContext);
  if (!userProfile) {
    return null;
  }

  return (
    <div
      onMouseEnter={() => props?.onEnter()}
      className={classNames("user-image", !userProfile?.avatarUrl && "user-image--placeholder")}
    >
      {userProfile?.avatarUrl ? (
        <img
          src={userProfile.avatarUrl}
          alt={`${userProfile?.firstName?.[0]}.${userProfile?.lastName?.[0]}`}
          className="user-image__image"
        />
      ) : (
        `${userProfile?.firstName?.[0]}${userProfile?.lastName?.[0]}`
      )}
    </div>
  );
}

function MegamenuHub({ title, text, linkText, linkTo, variant }) {
  const { setMenuOpen, setOpenTab } = useContext(HeaderContext);
  return (
    <article className={classNames("megamenu-hub", `megamenu-hub--${variant}`)}>
      <hgroup className="megamenu-hub__hgroup">
        <h2 className="megamenu-hub__title">{title}</h2>
        <p className="megamenu-hub__text">{text}</p>
      </hgroup>
      <Link
        className="megamenu-hub__link"
        to={linkTo}
        onClick={() => {
          setMenuOpen(false);
          setOpenTab("");
        }}
      >
        {linkText}
      </Link>
    </article>
  );
}

function MegamenuHubs() {
  const { labels } = useContext(LanguageContext);
  return (
    <ul className="megamenu__items megamenu-hubs">
      <li className="megamenu-hubs__item">
        <MegamenuHub
          variant={"blue"}
          linkText={labels?.BUSINESS_HUBS_BROWSE ?? "Browse business hubs"}
          linkTo={`${myHubsPageUrl}?section=business`}
          title={labels?.BUSINESS_HUBS ?? "Business hubs"}
          text={
            labels?.BUSINESS_HUBS_DESCRIPTION ??
            "Discover the latest news, resources, and insights on innovation and operations."
          }
        />
      </li>
      <li className="megamenu-hubs__item">
        <MegamenuHub
          variant={"green"}
          linkText={labels?.REGIONAL_HUBS_BROWSE ?? "Browse regional hubs"}
          linkTo={`${myHubsPageUrl}?section=regional`}
          title={labels?.REGIONAL_HUBS ?? "Regional hubs"}
          text={
            labels?.REGIONAL_HUBS_DESCRIPTION ??
            "Discover all your local information, services and initiatives."
          }
        />
      </li>
      <li className="megamenu-hubs__item">
        <MegamenuHub
          variant={"purple"}
          linkText={labels?.MY_HUBS_BROWSE ?? "browse your hubs"}
          linkTo={myHubsPageUrl}
          title={labels?.HOME_MY_HUBS ?? "My hubs"}
          text={
            labels?.MY_HUBS_DESCRIPTION_MENU ??
            "Get easy access to your regional and business Hubs."
          }
        />
      </li>
    </ul>
  );
}

function MegamenuContent({ openTabUrl, menu }) {
  const { setMenuOpen, setOpenTab } = useContext(HeaderContext);
  const { labels } = useContext(LanguageContext);

  if (openTabUrl === myHubsPageUrl) {
    return <MegamenuHubs />;
  }

  const menuItem = menu.find((t) => t.link === openTabUrl);

  return (
    <div className="megamenu__content">
      <div className="megamenu__description">
        <p className="megamenu__description-title">{menuItem.label}</p>
        <p className="megamenu__description-content">{getTabDescription(openTabUrl, labels)}</p>
      </div>
      <ul className="megamenu__items">
        {menuItem.children?.map((c) => {
          return (
            <li className="megamenu__item" key={c.label}>
              <Link
                className="megamenu__item-link"
                to={c.link}
                onClick={() => {
                  setMenuOpen(false);
                  setOpenTab("");
                }}
              >
                {c.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Megamenu({ menu, openTabUrl }) {
  return (
    <div className="header-new__megamenu-content megamenu">
      <MegamenuContent menu={menu} openTabUrl={openTabUrl} />
    </div>
  );
}

export function MyProfile(props) {
  const { logout, userProfile } = useContext(UserContext);
  const { labels } = useContext(LanguageContext);
  const { setMenuOpen, setOpenTab, setOpenUserSubmenu } = useContext(HeaderContext);
  return (
    <div
      className={classNames("header-new__my-profile header-my-profile", {
        "header-my-profile--desktop": props.desktop,
      })}
    >
      <div className="header-my-profile__title">{labels?.PROFILE ?? "My profile"}</div>
      <Link
        className="header-my-profile__user-info"
        to={"/profile"}
        onClick={() => {
          setMenuOpen(false);
          setOpenTab("");
          setOpenUserSubmenu(false);
        }}
      >
        <UserImage />
        <div className="header-my-profile__name">{`${userProfile?.firstName} ${userProfile?.lastName}`}</div>
        <Icon className="header-my-profile__arrow" name={"arrowhead"} />
      </Link>
      <div className="header-my-profile__links">
        <Link
          to="/contact-and-support"
          className="header-my-profile__highlighted cta-link"
          onClick={() => {
            setMenuOpen(false);
            setOpenTab("");
            setOpenUserSubmenu(false);
          }}
        >
          {labels?.CONTACT_AND_SUPPORT ?? "Contact and support"}
          <Icon name="info-tooltip" />
        </Link>
        <Link to="/login" className="header-my-profile__highlighted cta-link" onClick={logout}>
          {labels?.LOGOUT ?? "Logout"}
          <Icon name="arrow" />
        </Link>
      </div>
    </div>
  );
}

export function MegamenuMobile() {
  const { setMenuOpen, menu, selectedLanguage, updateLanguage, cmsLanguages } =
    useContext(HeaderContext);
  const [selectedSubmenu, setSelectedSubmenu] = useState("");
  const { labels } = useContext(LanguageContext);
  return (
    <div className="megamenu-mobile">
      <div className="megamenu-mobile__top">
        {selectedSubmenu && (
          <button className="megamenu-mobile__back" onClick={() => setSelectedSubmenu("")}>
            <Icon name={"arrowhead"} />
          </button>
        )}
        {selectedSubmenu && <div>{menu.find((c) => c.link === selectedSubmenu).label}</div>}
        <button onClick={() => setMenuOpen(false)} className="megamenu-mobile__close">
          <Icon name={"x-icon"} />
        </button>
      </div>
      <div className="megamenu-mobile__items-wrapper">
        {!selectedSubmenu && (
          <>
            <ul className="megamenu-mobile__items">
              {menu.map((i) => {
                if (i.link === myHubsPageUrl) {
                  return (
                    <li key={i.label} className="megamenu-mobile__item">
                      <button
                        className="megamenu-mobile__button megamenu-mobile__button--first-level"
                        onClick={() => {
                          setSelectedSubmenu(i.link);
                        }}
                      >
                        <span>{i.label}</span>
                        <Icon name={"arrowhead"} className="megamenu-mobile__next-page" />
                      </button>
                    </li>
                  );
                }

                if (i.children.length > 0) {
                  return (
                    <li key={i.label} className="megamenu-mobile__item">
                      <button
                        className="megamenu-mobile__button megamenu-mobile__button--first-level"
                        onClick={() => {
                          setSelectedSubmenu(i.link);
                        }}
                      >
                        <span>{i.label}</span>
                        <Icon name={"arrowhead"} className="megamenu-mobile__next-page" />
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={i.label} className="megamenu-mobile__item">
                    <Link
                      onClick={() => {
                        setMenuOpen(false);
                        setSelectedSubmenu("");
                      }}
                      className="megamenu-mobile__link megamenu-mobile__link--first-level"
                      to={i.link}
                    >
                      {i.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <MyProfile />
            <div className="megamenu-mobile__language">
              <div className="megamenu-mobile__language-left">{labels?.LANGUAGE ?? "Language"}</div>
              <div className="megamenu-mobile__language-right">
                <span className="megamenu-mobile__language-name">{selectedLanguage?.label}</span>
                <Icon name="globe" className="megamenu-mobile__language-icon" />
              </div>
              <select
                className="megamenu-mobile__select-language"
                value={selectedLanguage?.value}
                onChange={(e) => {
                  updateLanguage(cmsLanguages?.find((opt) => opt.value === e.target.value));
                }}
                aria-label="native-select-language"
              >
                {cmsLanguages?.map((option) => (
                  <option value={option.value} disabled={option.disabled} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="megamenu-mobile__share">
              <ShareWidget />
            </div>
          </>
        )}
        {selectedSubmenu && selectedSubmenu === myHubsPageUrl && <MegamenuHubs />}
        {selectedSubmenu && selectedSubmenu !== myHubsPageUrl && (
          <ul className="megamenu-mobile__items">
            {menu
              .find((i) => i.link === selectedSubmenu)
              .children.map((c) => {
                return (
                  <li className="megamenu-mobile__item">
                    <Link
                      className="megamenu-mobile__link"
                      to={c.link}
                      onClick={() => {
                        setMenuOpen(false);
                      }}
                    >
                      {c.label}
                    </Link>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
}

export function HeaderItem({
  item,
  setOpenTab,
  setMenuOpen,
  openTab,
  setOpenUserSubmenu,
  openUserSubmenu,
  languageMenuOpen,
}) {
  const classname = classNames("header-new__item", {
    "header-new__item--selected":
      openTab === item.link || window.location.pathname.startsWith(item.link),
    "header-new__item--open": (openTab || openUserSubmenu) && !languageMenuOpen,
    "header-new__item--no-hover": languageMenuOpen,
  });
  if (item.link === myHubsPageUrl || item.children.length > 0) {
    return (
      <div
        onMouseEnter={() => {
          if (languageMenuOpen) {
            return;
          }
          setOpenTab(item.link);
          setOpenUserSubmenu(false);
        }}
        className={classname}
      >
        {item.label}
      </div>
    );
  }
  return (
    <Link
      onClick={() => {
        setOpenTab("");
        setMenuOpen(false);
      }}
      onMouseEnter={() => {
        if (languageMenuOpen) {
          return;
        }
        setOpenTab("");
      }}
      to={item.link}
      className={classname}
    >
      {item.label}
    </Link>
  );
}
